<script setup lang="ts">
import { useTimeRangeManager } from "@/stores/dashboard/dashboard-time";
import {
	mdiChevronDoubleLeft,
	mdiChevronDoubleRight,
	mdiChevronLeft,
	mdiChevronRight,
	mdiEarth,
} from "@mdi/js";
import { secondsToPrettyString } from "@soft-db/data-formatter";
import { Button, Icon, Tooltip } from "@soft-db/ui";
import { DateTime } from "luxon";
import { TimeRange } from "../../stores";
import AppTimeRangeMenu from "../common/AppTimeRangeMenu.vue";
import AppTooltip from "../common/AppTooltip.vue";

const props = defineProps<{
	disableArrow?: boolean;
	disableReason?: string;
	disableCustom?: boolean;
}>();

const { t } = useI18n();
const timeManager = useTimeRangeManager();

const customRange = computed<TimeRange>({
	get: () => timeManager.range.value,
	set: ({ start, end }) => {
		if (timeManager.rangeMode.value !== "custom") {
			timeManager.updateRangeMode("custom");
		}

		timeManager.updateRange({
			start: start.toJSDate(),
			end: end.toJSDate(),
		});
	},
});

const ctrlPressed = useKeyModifier("Control");

const delta = computed(() => {
	return timeManager.zoomWindow.value.mapUnits((x) => x * (ctrlPressed.value ? 0.75 : 0.25));
});

const deltaFormatted = computed(() => secondsToPrettyString(delta.value.as("seconds")));

async function moveRangeWindow(direction: "forward" | "backward") {
	// We need to set to custom so movement is not controller by the time preset
	if (timeManager.rangeMode.value !== "custom") {
		timeManager.updateRangeMode("custom");
	}

	const fnName = direction === "forward" ? "plus" : "minus";

	let { start, end } = timeManager.zoomRange.value;

	start = start[fnName](delta.value);
	end = end[fnName](delta.value);

	const now = new Date();
	if (now.valueOf() < end.valueOf()) {
		end = timeManager.createDateTime(now);
		start = end.minus(delta.value);
	}

	const { range } = timeManager;

	if (start < range.value.start) {
		const distance = range.value.start.diff(start);
		timeManager.updateRange(
			{
				start,
				end: range.value.end.minus(distance),
			},
			false
		);
	}

	if (end > range.value.end) {
		const distance = end.diff(range.value.end);
		timeManager.updateRange(
			{
				start: range.value.start.plus(distance),
				end,
			},
			false
		);
	}

	await nextTick();

	timeManager.updateZoom({ start, end });
}

function moveBackward() {
	moveRangeWindow("backward");
}

function moveForward() {
	moveRangeWindow("forward");
}

const backwardTooltip = computed(() => {
	if (props.disableArrow) {
		return props.disableReason;
	} else {
		return t("dashboard.moveBackward", { n: deltaFormatted.value });
	}
});

const forwardTooltip = computed(() => {
	if (props.disableArrow) {
		return props.disableReason;
	} else {
		return t("dashboard.moveForward", { n: deltaFormatted.value });
	}
});

const now = useTimestamp({
	interval: 10_000,
});
const endTimeCloseToCurrentTime = computedEager(() => {
	return DateTime.fromMillis(now.value).diff(timeManager.range.value.end).as("minute") < 1;
});

const userTimezone = DateTime.now().zone;
const siteTimezone = computed(() => timeManager.range.value.start.zone);

const isSameTimezone = computed(() => {
	return userTimezone.name === siteTimezone.value.name;
});

const timezoneDisplay = computed(() => {
	return `${siteTimezone.value.name} (${siteTimezone.value.formatOffset(siteTimezone.value.offset(Date.now()), "short")})`;
});
</script>

<template>
	<div class="flex items-center gap-4">
		<AppTooltip>
			<Icon :class="isSameTimezone ? 'text-success' : 'text-warning'" :d="mdiEarth" />

			<template #tooltip>
				<div class="flex flex-col gap-1">
					<div v-if="!isSameTimezone">{{ t("dashboard.differentTimezone") }}</div>
					<div>
						<span class="font-semibold">{{ t("dashboard.siteTimezone") }}</span> :
						{{ timezoneDisplay }}
					</div>
				</div>
			</template>
		</AppTooltip>
		<div class="join flex">
			<Tooltip :tooltip="backwardTooltip">
				<span>
					<Button
						class="join-item"
						variant="outline"
						:disabled="disableArrow"
						@click="moveBackward"
					>
						<Icon :d="ctrlPressed ? mdiChevronDoubleLeft : mdiChevronLeft" />
					</Button>
				</span>
			</Tooltip>
			<AppTimeRangeMenu
				class="join-item border-r-0 focus:isolate"
				:range="customRange"
				:mode="timeManager.rangeMode.value"
				:disableCustom="disableCustom"
				@update:mode="(m) => timeManager.updateRangeMode(m)"
				@update:range="(r) => (customRange = r)"
			/>

			<Tooltip :tooltip="forwardTooltip">
				<span>
					<Button
						class="join-item"
						variant="outline"
						:disabled="disableArrow || endTimeCloseToCurrentTime"
						@click="moveForward"
					>
						<Icon :d="ctrlPressed ? mdiChevronDoubleRight : mdiChevronRight" />
					</Button>
				</span>
			</Tooltip>
		</div>
	</div>
</template>
